import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {
  getFirestore, collection, getDocs, doc, getDoc, query, where,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { app } from "../firebase";

const locales = { "pt-BR": ptBR };
const localizer = dateFnsLocalizer({
  format, parse, startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }), // Começa na Segunda-feira
  getDay, locales,
});

const diasDaSemana = ["domingo", "segunda", "terca", "quarta", "quinta", "sexta", "sabado"];

async function buscarHorarioFuncionamentoAtual(barbeariaId) {
  const db = getFirestore(app);
  const hoje = new Date();
  const nomeDia = diasDaSemana[hoje.getDay()];
  const ref = doc(db, `barbearia/${barbeariaId}/horarioFuncionamento/${nomeDia}`);
  const snap = await getDoc(ref);

  if (snap.exists()) {
    const data = snap.data();
    if (data.aberto) {
      return { horarioInicio: data.horarioInicio || "06:00", horarioTermino: data.horarioTermino || "23:59" };
    }
  }
  return { horarioInicio: "06:00", horarioTermino: "23:59" };
}

/* ============== ESTILOS CORRIGIDOS E ALINHADOS ============== */
/* ============== ESTILOS ATUALIZADOS ============== */
const StyledCalendarWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  /* --- 1. TOOLBAR (Cabeçalho com botões) --- */
  .rbc-toolbar {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    margin-bottom: 0;
    gap: 1rem;
    background: #fff;
  }

  .rbc-toolbar-label {
    font-size: 1.15rem;
    font-weight: 800;
    color: #3a79ed;
    text-transform: capitalize;
    text-align: center;
    flex-grow: 1;
  }

  .rbc-btn-group {
    display: inline-flex;
    gap: 0.5rem;
    box-shadow: none; border: none;
  }

  .rbc-toolbar button {
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #64748b;
    padding: 0.4rem 0.9rem;
    font-weight: 600;
    font-size: 0.85rem;
    border-radius: 6px !important;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #f8fafc;
      color: #334155;
      border-color: #cbd5e1;
    }
    &:active, &.rbc-active {
      background: #3a79ed;
      color: #fff;
      border-color: #3a79ed;
      box-shadow: 0 2px 4px rgba(58, 121, 237, 0.2);
    }
    &:focus { outline: none; }
  }

  /* --- 2. ESTRUTURA GERAL --- */
  --border-color: #e2e8f0;

  .rbc-calendar {
    border: 1px solid var(--border-color);
    border-radius: 12px;
    overflow: hidden;
  }

  .rbc-month-view, .rbc-time-view, .rbc-header, .rbc-time-header-content {
    border: none;
  }

  /* --- 3. VIEWS DE SEMANA/DIA (Week/Day) --- */
  .rbc-time-header {
    background: #f8fafc;
  }

  .rbc-header {
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.85rem;
    font-weight: 600;
    color: #64748b;
    text-transform: capitalize;
    padding: 0; /* Garante linha abaixo do dia */
  }

  .rbc-time-header.rbc-overflowing { border: none; }
  .rbc-time-header-gutter { background: #fff; }
  .rbc-allday-cell { display: none !important; }

  /* Gutter (Coluna de horas esquerda) */
  .rbc-time-gutter {
    border-right: 1px solid var(--border-color);
  }
  .rbc-time-gutter .rbc-timeslot-group {
    border-bottom: 1px solid #f1f5f9;
    background: #fff;
    color: #94a3b8;
    font-size: 0.75rem;
    font-weight: 500;
    display: flex; align-items: center; justify-content: center;
  }

  /* Grade principal */
  .rbc-day-slot { background: #fff; }
  .rbc-day-bg + .rbc-day-bg { border-left: 1px solid var(--border-color); }
  .rbc-timeslot-group {
    min-height: 60px !important;
    border-bottom: 1px solid #f1f5f9;
  }
  .rbc-day-slot .rbc-time-slot { border-top: 1px solid #f5f5f5; }
  .rbc-day-bg.rbc-today { background-color: rgba(58, 121, 237, 0.03); }

  /* Eventos (Card azul) */
  .rbc-event {
    background-color: #3a79ed !important;
    border: none;
    border-radius: 6px;
    padding: 2px 6px;
    box-shadow: 0 2px 4px rgba(58, 121, 237, 0.2);
    border-left: 3px solid rgba(255,255,255,0.3);
    opacity: 0.95;
    transition: transform 0.1s;
    
    &:hover {
      transform: scale(1.01);
      opacity: 1;
      z-index: 10;
    }
  }
  .rbc-event-content { font-size: 0.8rem; font-weight: 600; }
  .rbc-current-time-indicator { background-color: #ef4444; height: 2px; }

  /* Scrollbar */
  .rbc-time-content::-webkit-scrollbar { width: 6px; }
  .rbc-time-content::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 4px; }
  .rbc-time-content::-webkit-scrollbar-track { background: #f8fafc; }

  /* ==========================================================
     VISUALIZAÇÃO "AGENDA" (LISTA) - SEM CABEÇALHO
     ========================================================== */
  
  .rbc-agenda-view table.rbc-agenda-table {
    border: none !important;
    border-collapse: collapse;
    width: 100%;
  }

  /* --- OCULTAR O CABEÇALHO (Date, Time, Event) --- */
  .rbc-agenda-view table.rbc-agenda-table thead {
    display: none !important;
  }

  /* Linhas do corpo da tabela */
  .rbc-agenda-view table.rbc-agenda-table tbody > tr > td {
    padding: 16px 20px; /* Um pouco mais de espaço já que não tem cabeçalho */
    border-bottom: 1px solid #f1f5f9;
    border-top: none; 
    border-right: none;
    border-left: none;
    font-size: 0.9rem;
    color: #334155;
    vertical-align: middle;
    background-color: #fff;
  }

  /* Remove borda da última linha para ficar limpo no final do card */
  .rbc-agenda-view table.rbc-agenda-table tbody > tr:last-child > td {
    border-bottom: none;
  }

  /* Efeito Hover na linha */
  .rbc-agenda-view table.rbc-agenda-table tbody > tr:hover > td {
    background-color: #f8fafc;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  /* Coluna de Data */
  .rbc-agenda-date-cell {
    font-weight: 700;
    color: #1e293b !important;
    white-space: nowrap;
    width: 15%; /* Define larguras para alinhar melhor visualmente */
  }

  /* Coluna de Tempo */
  .rbc-agenda-time-cell {
    color: #64748b !important;
    font-weight: 500;
    white-space: nowrap;
    font-size: 0.85rem !important;
    width: 15%;
  }

  /* Coluna de Evento */
  .rbc-agenda-event-cell {
    font-weight: 600;
    color: #3a79ed !important;
    width: 70%;
  }

  /* Mensagem quando não há eventos */
  .rbc-agenda-view .rbc-agenda-empty {
    color: #94a3b8;
    padding: 3rem;
    text-align: center;
    font-size: 0.95rem;
    border: none;
  }

  @media (max-width: 768px) {
    /* No mobile, remove larguras fixas e ajusta padding */
    .rbc-agenda-date-cell, .rbc-agenda-time-cell, .rbc-agenda-event-cell {
        width: auto;
        display: block; /* Empilha: Data em cima, hora, depois evento */
        padding-bottom: 4px;
    }
    .rbc-agenda-view table.rbc-agenda-table tbody > tr > td {
        padding: 8px 16px;
        border: none;
    }
    .rbc-agenda-view table.rbc-agenda-table tbody > tr {
        border-bottom: 1px solid #e2e8f0;
        padding: 10px 0;
        display: block;
    }
  }
`
const CustomSelect = styled.select`
  padding: 10px 14px;
  font-size: 0.95rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background-color: #fff;
  color: #1e293b;
  margin-bottom: 1rem;
  width: 100%;
  outline: none;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  transition: all 0.2s;
  
  &:focus { 
    border-color: #3a79ed; 
    box-shadow: 0 0 0 3px rgba(58, 136, 237, 0.1); 
  }
`;

const SpinnerWrapper = styled.div`
  display: flex; justify-content: center; align-items: center; height: 100%;
`;
const Spinner = styled.div`
  border: 3px solid #f3f3f3; border-top: 3px solid #3a79ed; border-radius: 50%; width: 30px; height: 30px; animation: spin 1s linear infinite;
  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
`;

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth <= breakpoint : false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

export default function Agenda() {
  const [eventosPorBarbeiro, setEventosPorBarbeiro] = useState({});
  const [barbeiroSelecionado, setBarbeiroSelecionado] = useState("");
  const [barbeiros, setBarbeiros] = useState([]);
  const [horaInicio, setHoraInicio] = useState("08:00");
  const [horaFim, setHoraFim] = useState("18:00");
  const [cargo, setCargo] = useState(null);
  const [usuarioUid, setUsuarioUid] = useState(null);
  const [dataAtual, setDataAtual] = useState(new Date());
  const [viewAtual, setViewAtual] = useState("week");
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile(1024);

  useEffect(() => {
    setViewAtual((prev) => (isMobile ? "day" : prev === "day" ? "week" : prev));
  }, [isMobile]);

  useEffect(() => {
    const auth = getAuth(app);
    const db = getFirestore(app);

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) return;
      const uid = currentUser.uid;
      setUsuarioUid(uid);
      
      let barbeariaId = null;
      const q = query(collection(db, "barbearia"), where("criadorUid", "==", uid));
      const querySnapshot = await getDocs(q);
      let tipoUsuario = null;

      if (!querySnapshot.empty) {
        barbeariaId = querySnapshot.docs[0].id;
        tipoUsuario = "proprietario";
      } else {
        const barbeariasSnapshot = await getDocs(collection(db, "barbearia"));
        for (const docBarbearia of barbeariasSnapshot.docs) {
          const barbeiroRef = doc(db, `barbearia/${docBarbearia.id}/barbeiros/${uid}`);
          const barbeiroSnap = await getDoc(barbeiroRef);
          if (barbeiroSnap.exists()) {
            barbeariaId = docBarbearia.id;
            tipoUsuario = "barbeiro";
            break;
          }
        }
      }

      if (!barbeariaId || !tipoUsuario) return;
      setCargo(tipoUsuario);

      const horarios = await buscarHorarioFuncionamentoAtual(barbeariaId);
      setHoraInicio(horarios.horarioInicio);
      setHoraFim(horarios.horarioTermino);

      const snapshot = await getDocs(collection(db, `barbearia/${barbeariaId}/agendamentos`));
      const dados = {};
      
      snapshot.forEach((docSnap) => {
        const ag = docSnap.data();
        if (ag.status !== "confirmado") return;
        const barbeiroId = ag.barbeiroId || "desconhecido";
        if (tipoUsuario === "barbeiro" && barbeiroId !== uid) return;

        const [hour, minute] = (ag.horaInicio || "00:00").split(":");
        const startDate = new Date(`${ag.data}T${hour.padStart(2, "0")}:${minute.padStart(2, "0")}:00`);
        const endDate = new Date(startDate.getTime() + (ag.duracao || 30) * 60000);

        if (!dados[barbeiroId]) dados[barbeiroId] = [];
        dados[barbeiroId].push({
          id: docSnap.id,
          title: `${ag.servicoNome || "Serviço"} • ${ag.clienteNome || "Cliente"}`,
          start: startDate, end: endDate,
        });
      });

      setEventosPorBarbeiro(dados);

      if (tipoUsuario === "barbeiro") {
        setBarbeiroSelecionado(uid);
      } else {
        const barbeirosSnapshot = await getDocs(collection(db, `barbearia/${barbeariaId}/barbeiros`));
        const barbeirosData = barbeirosSnapshot.docs.map(d => ({ id: d.id, nome: d.data().nome || "Sem nome" }));
        setBarbeiros(barbeirosData);
        if (Object.keys(dados)[0]) setBarbeiroSelecionado(Object.keys(dados)[0]);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const eventos = eventosPorBarbeiro[barbeiroSelecionado] || [];

const formats = {
    // Formato do Título no modo Dia (ex: "Terça-feira, 02 de Dezembro")
    dayHeaderFormat: (date, culture, localizer) => 
      localizer.format(date, 'EEEE, dd \'de\' MMMM', culture),

    // Formato dos dias na vista Semana
    dayFormat: (date, culture, localizer) => 
      localizer.format(date, 'dd EEE', culture), 

    // Formato dos dias no cabeçalho da Semana
    weekdayFormat: (date, culture, localizer) => 
      localizer.format(date, 'EEE', culture),

    // Coluna das horas lateral
    timeGutterFormat: (date, culture, localizer) => 
      localizer.format(date, 'HH:mm', culture),

    // Intervalo de horas nos eventos
    eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
      `${localizer.format(start, 'HH:mm', culture)} - ${localizer.format(end, 'HH:mm', culture)}`,
  };

  return (
    <StyledCalendarWrapper>
      {loading ? (
        <SpinnerWrapper><Spinner /></SpinnerWrapper>
      ) : (
        <>
          {cargo === "proprietario" && (
            <CustomSelect value={barbeiroSelecionado} onChange={(e) => setBarbeiroSelecionado(e.target.value)}>
              <option value="" disabled>Selecione o Barbeiro</option>
              {barbeiros.map(b => <option key={b.id} value={b.id}>{b.nome}</option>)}
            </CustomSelect>
          )}

          <Calendar
            localizer={localizer}
            events={eventos}
            startAccessor="start"
            endAccessor="end"
            view={viewAtual}
            onView={setViewAtual}
            defaultView="week"
            date={dataAtual}
            onNavigate={setDataAtual}
            formats={formats}
            min={new Date(`1970-01-01T${horaInicio}:00`)}
            max={new Date(`1970-01-01T${horaFim}:00`)}
            messages={{
              today: "Hoje", previous: "<", next: ">", 
              week: "Semana", day: "Dia", month: "Mês", agenda: "Lista",
              showMore: (total) => `+${total} mais`,
              noEventsInRange: "Não há agendamentos."
            }}
            culture="pt-BR"
          />
        </>
      )}
    </StyledCalendarWrapper>
  );
}